import { useState } from "react";
import Icon from "components/Icon";
import SeloGooglePlay from "components/SeloGooglePlay";
import { appAndroidPublicado } from "config/appAndroid";
import { ehAndroidNoNavegador } from "services/plataforma";

const CHAVE_DISPENSA = "tarjaAppDispensadaEm";

// Quem fechou a tarja, ou já tocou no selo, não a vê de novo por um mês. Para sempre seria perder
// quem fechou por pressa. Toda visita seria insistência, numa página que a pessoa abre para outra
// coisa.
const DIAS_PARA_REAPARECER = 30;
const MS_POR_DIA = 24 * 60 * 60 * 1000;

// O storage pode lançar (aba anônima, dados do site bloqueados). Aí a tarja só não lembra da
// dispensa: aparece, e fechar a esconde até a próxima visita.
function dispensadaRecentemente() {
	try {
		const em = Number(localStorage.getItem(CHAVE_DISPENSA));
		return em > 0 && Date.now() - em < DIAS_PARA_REAPARECER * MS_POR_DIA;
	} catch {
		return false;
	}
}

function registrarDispensa() {
	try {
		localStorage.setItem(CHAVE_DISPENSA, String(Date.now()));
	} catch {
		// Sem storage: vale só para esta visita.
	}
}

/**
 * Convite para o app Android, logo abaixo do menu da Home. Aparece só no navegador de um Android.
 * O link da loja não serve em outro lugar, e por isso a decisão é pelo sistema, não pela largura da
 * tela (ver services/plataforma.js).
 *
 * Fica abaixo do NavBar, e não acima como os "smart app banners" de costume. O menu sanduíche e o
 * painel de notificações abrem em `top-[var(--altura-header)]`, que supõe o header colado no topo.
 * Com a tarja em cima, no scroll 0 o painel abriria por cima do próprio header. Aqui ela continua
 * sendo a primeira coisa da página, rola por baixo do header sticky e some.
 *
 * No fluxo, e não fixa: o rodapé já tem o banner de cookies fixo, e uma faixa fixa no topo
 * comeria altura de tela a página inteira. A decisão de mostrar é síncrona, então a tarja sai no
 * primeiro render e não empurra o hero depois (sem CLS). Também fica fora do `data-revelar`, pelo
 * mesmo motivo do hero: é a primeira dobra.
 *
 * Raciocínio em prompts/tarja-app-google-play.md.
 */
export default function TarjaApp() {
	const [visivel, setVisivel] = useState(
		() => appAndroidPublicado() && ehAndroidNoNavegador() && !dispensadaRecentemente()
	);

	if (!visivel) return null;

	const dispensar = () => {
		registrarDispensa();
		setVisivel(false);
	};

	return (
		<aside aria-label="Aplicativo Nova Faxina" className="border-b border-border bg-surface">
			<div className="mx-auto flex max-w-6xl items-center gap-3 py-2 pl-4 pr-1 sm:px-6 lg:px-8">
				{/* O ícone do app como ele aparece no aparelho (os círculos sobre branco, ver
				    android/app/src/main/res/mipmap-*): o fio é o que o recorta do fundo da faixa.
				    Abaixo de 360px ele sai, para o texto caber ao lado do selo. */}
				<img
					src="/images/nova-faxina-icone.svg"
					width="40"
					height="40"
					alt=""
					className="hidden h-10 w-10 shrink-0 rounded-xl bg-white p-0.5 ring-1 ring-border min-[360px]:block"
				/>
				<p className="min-w-0 flex-1 leading-tight">
					<span className="block text-[0.8125rem] font-semibold text-dark">Nova Faxina</span>
					<span className="block text-xs text-muted">Avisos na hora</span>
				</p>
				{/* Tocar no selo também conta como dispensa: quem foi à loja já viu o convite, e se
				    voltar sem instalar a tarja reaparece daqui a um mês. Só grava, sem esconder:
				    desmontar o link no meio do próprio clique arriscaria a navegação. */}
				<SeloGooglePlay origem="tarja-home" className="h-10" onClick={registrarDispensa} />
				<button
					type="button"
					onClick={dispensar}
					aria-label="Fechar aviso do aplicativo"
					className="grid h-11 w-11 shrink-0 place-items-center rounded-full text-muted transition hover:bg-subtle hover:text-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
				>
					<Icon name="close" className="h-5 w-5" />
				</button>
			</div>
		</aside>
	);
}
